import { Ticket } from '../components/types/ticketTypes';
import { WorkItem } from '../components/workitems/workItemTypes';

const API_BASE_URL = '/api';

export interface PaginationInfo {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface CustomerTicketsResponse {
  items: WorkItem[];
  pagination: PaginationInfo;
}

/**
 * Fetch all tickets from the API.
 * Handles both `{ items: [...] }` envelope and direct array responses.
 */
export async function getAllTickets(signal?: AbortSignal): Promise<Ticket[]> {
  const response = await fetch(`${API_BASE_URL}/tickets`, {
    method: 'GET',
    headers: { 'Accept': 'application/json' },
    signal,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch tickets: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  const items = Array.isArray(data) ? data : (data.items ?? []);
  return items as Ticket[];
}

/**
 * Search tickets via the API search parameter.
 */
export async function searchTickets(
  searchTerm: string,
  signal?: AbortSignal
): Promise<Ticket[]> {
  const params = new URLSearchParams({ search: searchTerm });
  const response = await fetch(`${API_BASE_URL}/tickets?${params}`, {
    method: 'GET',
    headers: { 'Accept': 'application/json' },
    signal,
  });

  if (!response.ok) {
    throw new Error(`Failed to search tickets: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  const items = Array.isArray(data) ? data : (data.items ?? []);
  return items as Ticket[];
}

/**
 * Fetch paginated tickets for a specific customer (all statuses, date descending).
 */
export async function getCustomerTickets(
  customerId: string,
  page: number = 1,
  pageSize: number = 10,
  signal?: AbortSignal
): Promise<CustomerTicketsResponse> {
  const params = new URLSearchParams({
    customerId,
    active: 'false',
    page: page.toString(),
    pageSize: pageSize.toString(),
  });

  const response = await fetch(`${API_BASE_URL}/tickets?${params}`, {
    method: 'GET',
    headers: { 'Accept': 'application/json' },
    signal,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch customer tickets: ${response.status} ${response.statusText}`);
  }

  return await response.json();
}
